<template>
  <div class="rich-text-editor">
    <!-- Toolbar -->
    <div class="toolbar border border-gray-300 dark:border-gray-600 rounded-t-md bg-gray-50 dark:bg-gray-700 p-2 flex flex-wrap gap-1">
      <!-- Text formatting -->
      <button
        @click="execCommand('bold')"
        :class="['toolbar-btn', { 'active': isActive('bold') }]"
        title="Bold"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 12h8a4 4 0 100-8H6v8zm0 0h8a4 4 0 110 8H6v-8z" />
        </svg>
      </button>
      
      <button
        @click="execCommand('italic')"
        :class="['toolbar-btn', { 'active': isActive('italic') }]"
        title="Italic"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      </button>
      
      <button
        @click="execCommand('underline')"
        :class="['toolbar-btn', { 'active': isActive('underline') }]"
        title="Underline"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      </button>

      <div class="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      <!-- Headings -->
      <select
        @change="setHeading($event.target.value)"
        class="toolbar-select"
        title="Heading"
      >
        <option value="">Normal</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="h4">Heading 4</option>
        <option value="h5">Heading 5</option>
        <option value="h6">Heading 6</option>
      </select>

      <div class="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      <!-- Lists -->
      <button
        @click="execCommand('insertUnorderedList')"
        :class="['toolbar-btn', { 'active': isActive('insertUnorderedList') }]"
        title="Bullet List"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
      
      <button
        @click="execCommand('insertOrderedList')"
        :class="['toolbar-btn', { 'active': isActive('insertOrderedList') }]"
        title="Numbered List"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      </button>

      <div class="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      <!-- Links -->
      <button
        @click="insertLink"
        class="toolbar-btn"
        title="Insert Link"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      </button>

      <div class="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      <!-- Clear formatting -->
      <button
        @click="execCommand('removeFormat')"
        class="toolbar-btn"
        title="Clear Formatting"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Editor content -->
    <div
      ref="editor"
      class="editor-content border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-md bg-white dark:bg-gray-800 p-4 min-h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      contenteditable="true"
      @input="handleInput"
      @keydown="handleKeydown"
      @paste="handlePaste"
      @blur="handleBlur"
    ></div>

    <!-- Link dialog -->
    <div v-if="showLinkDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
        <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Insert Link</h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">URL</label>
            <input
              v-model="linkUrl"
              type="url"
              placeholder="https://example.com"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Text (optional)</label>
            <input
              v-model="linkText"
              type="text"
              placeholder="Link text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>
        <div class="flex justify-end space-x-3 mt-6">
          <button
            @click="cancelLink"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            @click="confirmLink"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'

export default {
  name: 'RichTextEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Start typing...'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const editor = ref(null)
    const showLinkDialog = ref(false)
    const linkUrl = ref('')
    const linkText = ref('')
    const savedSelection = ref(null)

    // Simple HTML sanitization function
    const sanitizeHTML = (html) => {
      // Create a temporary div to parse HTML
      const temp = document.createElement('div')
      temp.innerHTML = html
      
      // Remove script tags and event handlers
      const scripts = temp.querySelectorAll('script')
      scripts.forEach(script => script.remove())
      
      // Remove event handlers from all elements
      const allElements = temp.querySelectorAll('*')
      allElements.forEach(el => {
        const attrs = el.attributes
        for (let i = attrs.length - 1; i >= 0; i--) {
          const attr = attrs[i]
          if (attr.name.startsWith('on') || attr.name === 'javascript:') {
            el.removeAttribute(attr.name)
          }
        }
      })
      
      return temp.innerHTML
    }

    const handleInput = () => {
      if (editor.value) {
        const content = editor.value.innerHTML
        const sanitizedContent = sanitizeHTML(content)
        
        // Only emit if content actually changed
        if (sanitizedContent !== props.modelValue) {
          emit('update:modelValue', sanitizedContent)
        }
      }
    }

    const handleBlur = () => {
      if (editor.value) {
        const content = editor.value.innerHTML
        const sanitizedContent = sanitizeHTML(content)
        emit('update:modelValue', sanitizedContent)
      }
    }

    const execCommand = (command, value = null) => {
      document.execCommand(command, false, value)
      editor.value?.focus()
      handleInput()
    }

    const isActive = (command) => {
      return document.queryCommandState(command)
    }

    const setHeading = (tag) => {
      if (tag) {
        execCommand('formatBlock', `<${tag}>`)
      } else {
        execCommand('formatBlock', '<p>')
      }
    }

    const saveSelection = () => {
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        savedSelection.value = selection.getRangeAt(0)
      }
    }

    const restoreSelection = () => {
      if (savedSelection.value) {
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(savedSelection.value)
      }
    }

    const insertLink = () => {
      saveSelection()
      showLinkDialog.value = true
      linkUrl.value = ''
      linkText.value = ''
    }

    const confirmLink = () => {
      if (linkUrl.value) {
        restoreSelection()
        const text = linkText.value || linkUrl.value
        const linkHTML = `<a href="${linkUrl.value}" target="_blank" rel="noopener noreferrer">${text}</a>`
        execCommand('insertHTML', linkHTML)
        showLinkDialog.value = false
      }
    }

    const cancelLink = () => {
      showLinkDialog.value = false
    }

    const handleKeydown = (event) => {
      // Handle Enter key for new paragraphs
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        execCommand('insertParagraph')
      }
    }

    const handlePaste = (event) => {
      event.preventDefault()
      const text = event.clipboardData.getData('text/plain')
      execCommand('insertText', text)
    }

    const updateEditorContent = () => {
      if (editor.value && props.modelValue !== editor.value.innerHTML) {
        editor.value.innerHTML = props.modelValue || ''
      }
    }

    onMounted(() => {
      updateEditorContent()
      if (editor.value) {
        editor.value.setAttribute('data-placeholder', props.placeholder)
      }
    })

    watch(() => props.modelValue, updateEditorContent)

    return {
      editor,
      showLinkDialog,
      linkUrl,
      linkText,
      handleInput,
      handleBlur,
      execCommand,
      isActive,
      setHeading,
      insertLink,
      confirmLink,
      cancelLink,
      handleKeydown,
      handlePaste
    }
  }
}
</script>

<style scoped>
.rich-text-editor {
  @apply relative;
}

.toolbar-btn {
  @apply p-2 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2;
}

.toolbar-btn.active {
  @apply bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400;
}

.toolbar-select {
  @apply px-2 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600;
}

.editor-content {
  @apply text-gray-900 dark:text-gray-100;
}

.editor-content:empty:before {
  content: attr(data-placeholder);
  @apply text-gray-400 dark:text-gray-500 pointer-events-none;
}

.editor-content:focus {
  @apply outline-none;
}

/* Ensure proper spacing in editor */
.editor-content p {
  @apply mb-2;
}

.editor-content p:last-child {
  @apply mb-0;
}

.editor-content ul, .editor-content ol {
  @apply mb-2 pl-6;
}

.editor-content li {
  @apply mb-1;
}

.editor-content h1, .editor-content h2, .editor-content h3,
.editor-content h4, .editor-content h5, .editor-content h6 {
  @apply font-bold mb-2;
}

.editor-content h1 { @apply text-2xl; }
.editor-content h2 { @apply text-xl; }
.editor-content h3 { @apply text-lg; }
.editor-content h4 { @apply text-base; }
.editor-content h5 { @apply text-sm; }
.editor-content h6 { @apply text-xs; }

.editor-content blockquote {
  @apply border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400 mb-2;
}

.editor-content code {
  @apply bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono;
}

.editor-content pre {
  @apply bg-gray-100 dark:bg-gray-700 p-3 rounded mb-2 overflow-x-auto;
}

.editor-content a {
  @apply text-blue-600 dark:text-blue-400 underline;
}

.editor-content table {
  @apply border-collapse border border-gray-300 dark:border-gray-600 mb-2;
}

.editor-content th, .editor-content td {
  @apply border border-gray-300 dark:border-gray-600 px-3 py-2;
}

.editor-content th {
  @apply bg-gray-100 dark:bg-gray-700 font-semibold;
}
</style> 